'use client'

import type { SummaryCardData } from '@/feature/sales/components/sales-summary-card/SalesSummaryCard.client'
import { SalesSummaryCard } from '@/feature/sales/components/sales-summary-card/SalesSummaryCard.client'
import { APOLLO_CLIENT_SIDE_OPTIONS } from '@/lib/apollo/clients/apolloClient.client'
import { useIsAuthenticated } from '@/lib/auth/client/AuthProvider.client'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { Card } from '@/ui/cards/card/Card'
import { useQuery } from '@apollo/client'
import { gql } from '@gql-generated/gql'
import type { ShippingFeeTag_FeeFragment } from '@gql-generated/graphql'
import { CartSummaryPricingDocument } from '@gql-generated/graphql'
import { memo } from 'react'
import { CartSummaryPricingSkeleton } from './CartSummaryPricingSkeleton.client'

gql(/* GraphQL */ `
  fragment CartSummaryPricing_Cart on Cart {
    id
    totalExclTax
    totalDiscount
    totalEcoPart
  }

  fragment CartSummaryPricing_Shipping on Shipping {
    fee {
      ... on ShippingFeeFree {
        isFranco
      }
      ... on ShippingFeeWithAmount {
        amount
      }
    }
  }

  fragment CartSummaryPricing_Order on Order {
    __typename
    payment {
      code
    }
    delivery {
      fee {
        ... on ShippingFeeFree {
          isFranco
        }
        ... on ShippingFeeWithAmount {
          amount
        }
      }
    }
    totalAmount
    totalEcoPart
    totalDiscount
    totalOrderedProducts
  }
`)

gql(/* GraphQL */ `
  query CartSummaryPricing($webRef: String!, $isOrderRequest: Boolean!) {
    currentUser {
      checkout @skip(if: $isOrderRequest) {
        cart(forceRefresh: false) {
          ...CartSummaryPricing_Cart
        }
        shipping {
          ...CartSummaryPricing_Shipping
        }
      }
      orderRequests(
        input: {
          filter: [{ type: STRING, field: "webRef", operator: EQUAL, stringValue: $webRef }]
        }
      ) @include(if: $isOrderRequest) {
        items {
          ...CartSummaryPricing_Order
          subtotalExclTax
          payment {
            orderReference
          }
        }
      }
    }
  }
`)

export type CartSummaryPricingProps = {
  className?: string
  orderRequestWebRef?: string
  children?: React.ReactElement
  payment?: ShippingFeeTag_FeeFragment | null
}

export const CartSummaryPricing = memo(function CartSummaryPricing({
  className,
  children,
  orderRequestWebRef: webRef = '',
}: CartSummaryPricingProps) {
  const { t } = useTranslation(['salesSummaryCard'])
  const isOrderRequest = !!webRef
  const isAuthenticated = useIsAuthenticated()

  const { data, error, loading } = useQuery(CartSummaryPricingDocument, {
    ...APOLLO_CLIENT_SIDE_OPTIONS,
    variables: { webRef, isOrderRequest },
    skip: !isAuthenticated,
  })

  if (loading) return <CartSummaryPricingSkeleton>{children}</CartSummaryPricingSkeleton>

  const cartData = data?.currentUser?.checkout?.cart
  const orderRequest = data?.currentUser?.orderRequests?.items?.[0]

  if (!!error?.graphQLErrors.length || (!cartData?.id && !orderRequest)) {
    return (
      <Card className={className} radius="sm" padding="lg" skin="body">
        {t('salesSummaryCard:error')}
      </Card>
    )
  }

  const infos = cartData || orderRequest
  const checkout = data?.currentUser?.checkout
  const fee = orderRequest?.delivery?.fee ?? checkout?.shipping?.fee

  const summaryData: SummaryCardData = {
    totalExclTax: isOrderRequest ? orderRequest?.totalAmount : cartData?.totalExclTax,
    totalDiscount: infos?.totalDiscount || 0,
    totalEcoPart: infos?.totalEcoPart || 0,
    paymentCode: orderRequest?.payment?.code,
    fee,
  }

  return (
    <SalesSummaryCard
      className={className}
      compact
      data={summaryData}
      divider={false}
      showTitle={false}
    >
      {children}
    </SalesSummaryCard>
  )
})
